<div class="main-container">
  <h2 class="page-header">Webhook Management</h2>

  <!-- PAY10 admin: merchant search bar (top) -->
  <div class="search-container admin-search" *ngIf="role === Role.PAY10_ADMIN">
    <div class="search-input-wrapper">
      <ng-container *ngIf="validatedMerchant; else searchInput">
        <span class="merchant-chip-text">{{ validatedMerchant.merchantName }} ({{ validatedMerchant.merchantId }})</span>
        <i class="pe-7s-close search-icon clear-icon" role="button"
           (click)="clearMerchantSearch()" aria-label="Clear merchant"></i>
      </ng-container>
      <ng-template #searchInput>
        <input type="text" class="search-input"
               placeholder="Search by Merchant Name or Merchant ID"
               [(ngModel)]="merchantSearchQuery"
               (ngModelChange)="onAdminSearchChange()"
               aria-label="Search merchants">
        <i class="pe-7s-search search-icon"
           role="button" (click)="searchMerchant()"></i>
        <i *ngIf="merchantSearchQuery" class="pe-7s-close search-icon clear-icon"
           role="button" (click)="clearMerchantSearch()"></i>
      </ng-template>
    </div>
    <div class="field-error" *ngIf="merchantError">
      <i class="pe-7s-attention"></i>
      <span>{{ merchantError }}</span>
    </div>
  </div>

  <!-- Toolbar: visible only for non-admin OR admin with validated merchant + webhooks -->
  <div class="toolbar" *ngIf="showToolbar">
    <h3 class="toolbar-left">{{ currentEnv }}</h3>
    <div class="toolbar-right">
      <div class="export-dropdown">
        <button type="button" class="export-button" (click)="openHistoryPanel()">
          <span>History</span>
        </button>
      </div>
      <div class="export-dropdown">
        <button type="button" class="export-button" (click)="exportWebhooks()">
          <span>Export</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Admin State A: pre-search (hasn't searched yet) -->
  <section class="webhook-section empty-state-section" *ngIf="role === Role.PAY10_ADMIN && (!hasSearched || (hasSearched && !validatedMerchant))">
    <div class="empty-state-card">
      <img src="assets/icons/Search-Glass.svg" alt="" class="empty-illustration" />
      <h3 class="empty-title">Find Merchant Webhooks</h3>
      <p class="empty-subtitle">Search for a merchant to view their webhook configuration details.</p>
    </div>
  </section>

  <!-- Admin State B: searched but merchant not found OR merchant has no webhooks -->
  <section class="webhook-section empty-state-section" *ngIf="role === Role.PAY10_ADMIN && hasSearched && validatedMerchant && !hasWebhooks">
    <div class="empty-state-card">
      <img src="assets/icons/Setting.svg" alt="" class="empty-illustration" />
      <h3 class="empty-title">No Webhooks Configured</h3>
      <p class="empty-subtitle">Add a webhook endpoint to receive real-time payment and refund status updates.</p>
      <button type="button" class="add-webhook-btn" (click)="addWebhook()">
        <span class="plus">+</span>
        <span>Add Webhook</span>
      </button>
    </div>
  </section>

  <!-- Webhook Section (current environment) - non-admin always, admin only when validated + has webhooks -->
  <section class="webhook-section" *ngIf="role !== Role.PAY10_ADMIN || (validatedMerchant && hasWebhooks)">
    <div class="card">
      <ng-container *ngFor="let eventType of eventTypes; let isLast = last">
        <div class="webhook-row">
          <div class="event-type-column">
            <span class="event-type-label">{{ eventType }}</span>
          </div>
          <div class="webhook-details-column">
            @let webhookDetail = getWebhooksByEnv(currentEnv).webhooks[eventType];
            <div class="detail-row">
              <span class="detail-label">Merchant Name</span>
              <span class="detail-value">{{ webhookDetail?.merchantName || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Merchant ID</span>
              <span class="detail-value">{{ webhookDetail?.merchantId || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Webhook</span>
              <span class="detail-value">{{ webhookDetail?.url || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Status</span>
              <span class="detail-value">{{ webhookDetail?.status || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Last Update On</span>
              <span class="detail-value">{{ webhookDetail?.updatedAt || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Last Update By</span>
              <span class="detail-value">{{ webhookDetail?.updatedBy || '-' }}</span>
            </div>
          </div>
          <div class="detail-actions">
            <button type="button" class="action-btn" [disabled]="!isEditAndTestAble(currentEnv)" (click)="openTestDialog(currentEnv, eventType)">Test</button>
            <button type="button" class="action-btn" [disabled]="!isEditAndTestAble(currentEnv)" (click)="openEditDialog(currentEnv)">Edit</button>
          </div>
        </div>
        <div class="divider" *ngIf="!isLast"></div>
      </ng-container>
    </div>
  </section>
</div>

<!-- Right-side History Drawer -->
<div class="history-backdrop" *ngIf="showHistoryPanel" (click)="onHistoryBackdrop($event)" (keydown.escape)="closeHistoryPanel()">
  <aside class="history-panel" (click)="$event.stopPropagation()" aria-label="History panel" (keydown)="onKeyDownHistoryPanel($event)">
    <header class="history-header">
      <h3>History</h3>
    </header>

    <div class="history-scroll" #historyScrollContainer (scroll)="onHistoryScroll()">
      <ng-container *ngFor="let item of historyItems">
        <div class="history-item">
          <div class="history-action" [ngClass]="getHistoryTypeLabel(item).toLowerCase()">{{ getHistoryTypeLabel(item) }}</div>
          <div class="history-message" [innerHTML]="getHistoryMessageHTML(item)"></div>
          <div class="history-datetime">{{ formatHistoryTime(item) }}</div>
          <div class="history-actor" *ngIf="getHistoryActor(item)">{{ getHistoryActor(item) }}</div>
        </div>
      </ng-container>

      <div class="history-loading" *ngIf="historyLoading">Loading…</div>
      <div class="history-empty" *ngIf="!historyLoading && historyItems.length === 0">No history yet</div>
      <div class="history-end" *ngIf="!historyLoading && !historyHasMore && historyItems.length > 0">End of history</div>
    </div>

    <footer class="history-actions">
      <button type="button" class="history-button" (click)="exportHistory()">Export</button>
      <button type="button" class="history-button" (click)="closeHistoryPanel()">Close</button>
    </footer>
  </aside>
</div>

<!-- Edit Webhooks Dialog -->
<app-webhook-edit-dialog
  [isVisible]="isEditDialogVisible"
  (isVisibleChange)="isEditDialogVisible = $event"
  [mode]="editDialogMode"
  [currentEnv]="currentEnv"
  [merchantName]="editMerchantName"
  [merchantId]="editMerchantId"
  [showMerchantInfo]="role === Role.PAY10_ADMIN && editDialogMode === 'edit'"
  [saleUrl]="editSaleUrl"
  [refundUrl]="editRefundUrl"
  (success)="onEditDialogSuccess()"
  (failed)="onEditDialogFailed($event)">
</app-webhook-edit-dialog>

<!-- Test Webhooks Dialog -->
<app-webhook-test-dialog
  [isVisible]="isTestDialogVisible"
  (isVisibleChange)="isTestDialogVisible = $event"
  [eventType]="testEventType"
  [merchantId]="testMerchantId"
  [webhookUrl]="testWebhookUrl"
  (testResult)="onTestDialogResult($event)">
</app-webhook-test-dialog>
